import { useEffect, useRef, useState } from 'react'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import dayjs from 'dayjs'
import { computeQuickRangeBounds, parseCustomDateTime } from '../../utils/timeRange'

// Scoped MUI theme so the pickers pick up the product palette instead of MUI blue.
const pickerTheme = createTheme({
  palette: {
    primary: { main: '#0b7667' },
    background: { paper: '#f9fafa' },
    text: { primary: '#272d37', secondary: '#616a79' },
  },
})

// Figma "Time Range Selection": custom ranges are capped at one month.
const MAX_CUSTOM_RANGE_MS = 31 * 24 * 60 * 60 * 1000
// Custom ranges must also fall inside the extraction window: today back 30 days.
const CUSTOM_WINDOW_MS = 30 * 24 * 60 * 60 * 1000

const QUICK_RANGES = [
  'Last 30 min - Realtime',
  'Last 30 min',
  'Last 1 hr',
  'Last 3 hr',
  'Last 12 hr',
  'Last 1 day',
  'Last 7 days',
  'Last 30 days',
  'Today',
  'Yesterday',
  'This week',
  'Previous week',
  'This month',
  'Previous month',
]

const RadioTab = ({ label, checked, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex flex-1 items-center gap-1 cursor-pointer border-none bg-transparent text-left"
  >
    <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${checked ? 'border-[#0b7667]' : 'border-[#616a79] bg-[#f9fafa]'}`}>
      {checked && <span className="w-2 h-2 rounded-full bg-[#0b7667]" />}
    </span>
    <span className="text-sm font-normal text-[#272d37] whitespace-nowrap">{label}</span>
  </button>
)

const pad2 = (n) => String(n).padStart(2, '0')
const formatCustomDateTime = (d) =>
  `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`

// MUI DateTimePicker wired to the same string contract the rest of the panel
// uses (dd-mm-yyyy hh:mm) — parse/validation in handleApply stays unchanged.
// disablePortal keeps the popup inside the panel's DOM so the outer
// outside-click handler doesn't close the whole dropdown on a calendar click.
const DateTimeInput = ({ value, onChange, minDateTime, maxDateTime }) => {
  const parsed = parseCustomDateTime(value)
  return (
    <DateTimePicker
      value={parsed ? dayjs(parsed) : null}
      minDateTime={minDateTime}
      maxDateTime={maxDateTime}
      onChange={(next) => {
        if (!next) {
          onChange('')
          return
        }
        if (next.isValid()) onChange(formatCustomDateTime(next.toDate()))
      }}
      format="DD-MM-YYYY HH:mm"
      ampm={false}
      slotProps={{
        popper: { disablePortal: true },
        textField: {
          size: 'small',
          placeholder: 'dd-mm-yyyy hh:mm',
          sx: {
            flex: 1,
            minWidth: 0,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#f9fafa',
              fontSize: '0.875rem',
              color: '#272d37',
              '& fieldset': { borderColor: '#b1bbcb' },
              '&:hover fieldset': { borderColor: '#b1bbcb' },
              '&.Mui-focused fieldset': { borderColor: '#0b7667', borderWidth: '1px' },
            },
          },
        },
      }}
    />
  )
}

const TimeRangeDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState('quick')
  const [quickValue, setQuickValue] = useState(QUICK_RANGES.includes(value) ? value : QUICK_RANGES[0])
  const [quickMenuOpen, setQuickMenuOpen] = useState(false)
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [customError, setCustomError] = useState('')
  const containerRef = useRef(null)

  const switchMode = (next) => {
    setMode(next)
    setCustomError('')
  }

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false)
        setQuickMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const closePanel = () => {
    setOpen(false)
    setQuickMenuOpen(false)
  }

  const handleApply = () => {
    if (mode === 'quick') {
      const { from, to } = computeQuickRangeBounds(quickValue)
      onChange({ label: quickValue, from, to })
      closePanel()
      return
    }
    const from = parseCustomDateTime(customFrom)
    const to = parseCustomDateTime(customTo)
    if (!from || !to) {
      setCustomError('Enter both dates as dd-mm-yyyy hh:mm')
      return
    }
    if (from >= to) {
      setCustomError('"From" must be earlier than "To"')
      return
    }
    if (to - from > MAX_CUSTOM_RANGE_MS) {
      setCustomError('Maximum one month of range can be selected.')
      return
    }
    const now = new Date()
    if (to > now) {
      setCustomError('"To" cannot be in the future')
      return
    }
    if (from < new Date(now.getTime() - CUSTOM_WINDOW_MS)) {
      setCustomError('Only data from the last 30 days can be extracted')
      return
    }
    setCustomError('')
    onChange({ label: `${customFrom} - ${customTo}`, from, to })
    closePanel()
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        className="flex items-center gap-2 h-9 px-3 rounded border border-[#dfe4ed] bg-[#f2f4f7] cursor-pointer text-sm font-medium text-[#272d37]"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="material-icons text-base">schedule</span>
        {value}
        <span className="material-icons text-base">expand_more</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 bg-[#f9fafa] rounded-md shadow-lg flex flex-col w-[524px]">
          <div className="flex items-center gap-4 border-b border-[#dfe4ed] pl-4 pr-2.5 py-2">
            <span className="flex-1 text-sm font-semibold text-[#272d37]">Time Range Selection</span>
            <button
              type="button"
              onClick={closePanel}
              className="flex items-center justify-center p-2 rounded-full border-none bg-transparent cursor-pointer hover:bg-[#f2f4f7]"
            >
              <span className="material-icons text-xl text-[#272d37]">close</span>
            </button>
          </div>

          <div className="flex flex-col gap-2 p-4 w-full">
            <div className="flex gap-2 items-start w-full">
              <RadioTab label="Quick ranges" checked={mode === 'quick'} onClick={() => switchMode('quick')} />
              <RadioTab label="Custom ranges" checked={mode === 'custom'} onClick={() => switchMode('custom')} />
            </div>

            {mode === 'quick' ? (
              <div className="relative w-full">
                <button
                  type="button"
                  onClick={() => setQuickMenuOpen((o) => !o)}
                  className={`flex items-center gap-1 w-full bg-[#f9fafa] border rounded px-2 py-2.5 cursor-pointer text-left ${quickMenuOpen ? 'border-[#0b7667]' : 'border-[#b1bbcb]'}`}
                >
                  <span className="flex-1 text-sm text-[#272d37] truncate">{quickValue}</span>
                  <span className="material-icons text-xl text-[#616a79] shrink-0">expand_more</span>
                </button>
                {quickMenuOpen && (
                  <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-[#f9fafa] rounded-lg shadow-lg py-2 max-h-72 overflow-y-auto">
                    {QUICK_RANGES.map((range) => (
                      <button
                        key={range}
                        type="button"
                        onClick={() => {
                          setQuickValue(range)
                          setQuickMenuOpen(false)
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-[#272d37] bg-transparent border-none cursor-pointer hover:bg-[#f2f4f7]"
                      >
                        {range}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-1.5 w-full">
                <ThemeProvider theme={pickerTheme}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="flex items-center gap-2 w-full">
                      <DateTimeInput
                        value={customFrom}
                        onChange={(v) => { setCustomFrom(v); setCustomError('') }}
                        minDateTime={dayjs(new Date(Date.now() - CUSTOM_WINDOW_MS))}
                        maxDateTime={dayjs()}
                      />
                      <span className="text-sm font-normal text-[#272d37]">-</span>
                      <DateTimeInput
                        value={customTo}
                        onChange={(v) => { setCustomTo(v); setCustomError('') }}
                        minDateTime={dayjs(new Date(Date.now() - CUSTOM_WINDOW_MS))}
                        maxDateTime={dayjs()}
                      />
                    </div>
                  </LocalizationProvider>
                </ThemeProvider>
                {customError ? (
                  <span className="text-xs font-normal text-[#d51d10]">{customError}</span>
                ) : (
                  <span className="text-xs font-normal text-[#616a79]">Note: Only data from today to the last 30 days can be extracted.</span>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end border-t border-[#dfe4ed] px-4 py-2">
            <button
              type="button"
              onClick={handleApply}
              className="h-10 px-4 rounded bg-[#0b7667] text-sm font-medium text-[#f9fafa] cursor-pointer border-none"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default TimeRangeDropdown
